import React, { useState, useEffect } from 'react';
import { TextField } from '@material-ui/core';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import useDebounce from '@forvalter/utils/useDebounce';
import { getLocalizedLabel } from '../../utils';
import type { FormField } from '../../interfaces';

interface DateFieldProps {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    classes: {
        editableField: string;
        readOnlyField: string;
    };
}

export const DateField: React.FC<DateFieldProps> = ({
    field,
    isReadOnly,
    isRequired,
    classes,
}) => {
    const { id, label } = field;
    const { values, setFieldValue, getFieldMeta, handleBlur } =
        useFormikContext<Record<string, unknown>>();

    const fieldValue = (values as Record<string, unknown>)[id as string];
    const meta = getFieldMeta(id as string);

    // Local state for immediate UI updates
    const [localValue, setLocalValue] = useState(fieldValue);

    // Use debounced value for Formik updates
    const { debouncedValue } = useDebounce(localValue, 300);

    // Update local state when field value changes from outside
    useEffect(() => {
        setLocalValue(fieldValue);
    }, [fieldValue]);

    // Update Formik when debounced value changes
    useEffect(() => {
        if (debouncedValue !== fieldValue) {
            setFieldValue(id as string, debouncedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(event.target.value);
    };

    const handleFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Immediately sync on blur to ensure validation
        setFieldValue(id as string, localValue);
        handleBlur(event);
    };

    return (
        <TextField
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            name={id}
            value={localValue ?? ''}
            onChange={handleChange}
            onBlur={handleFieldBlur}
            variant="outlined"
            id={id}
            label={getLocalizedLabel(`project.formFields.${id}`, label, id)}
            type="date"
            fullWidth={true}
            required={isRequired}
            disabled={isReadOnly}
            InputLabelProps={{
                shrink: true,
            }}
            helperText={meta.touched ? meta.error : ''}
            error={meta.touched && Boolean(meta.error)}
        />
    );
};
